import React, { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardCheck,
  Copy,
  Check,
  Download,
  Loader2,
} from 'lucide-react';

import { BackendAPI } from '../lib/api';
import { triggerEsxDownload } from '../lib/esxDownload';
import {
  CompletenessReport,
  EstimateHeaderMetadata,
  ParsedEstimateResponse,
  Project,
  ProjectMatchResponse,
} from '../types';
import { useSessionState } from '../hooks/useSessionState';
import useProjectStore from '../stores/projectStore';
import {
  UploadArea,
  RoomSection,
  OutOfCoverageSection,
  ParseWarningBanner,
  ParserDiagnosticsBanner,
  ParseVerificationView,
} from '../components/CompletenessComponents';
import PostParseProjectDialog from '../components/completeness/PostParseProjectDialog';

// ================================================================= //
//          Completeness Analysis Page                                  //
// ================================================================= //
// Main page for the completeness engine. Allows users to upload an   //
// estimate file (PDF or Excel), configure analysis parameters, and   //
// view gap analysis results with color-coded inline suggestions.     //
// ================================================================= //

export default function CompletenessPage() {
  // Reason: Persist primary page state across tab switches so parsed estimates
  // and reports survive navigating to Scope Builder and back. Keyed by
  // projectId. When no project is selected the page runs in scratchpad
  // mode: keys are null and useSessionState behaves as plain in-memory
  // state, so an unattached upload doesn't pin itself to a shared
  // "no project" bucket that haunts later visits (PRP-supp).
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const reportKey = projectId ? `completeness:report:${projectId}` : null;
  const parsedKey = projectId ? `completeness:parsed:${projectId}` : null;
  const configKey = projectId ? `completeness:config:${projectId}` : null;
  const dismissedKey = projectId ? `completeness:dismissed:${projectId}` : null;

  // --- Persisted output state ---
  const [report, setReport] = useSessionState<CompletenessReport | null>(reportKey, null);
  const [parsed, setParsed] = useSessionState<ParsedEstimateResponse | null>(parsedKey, null);

  // --- Persisted config ---
  // PRP-INTAKE-1 (I3): the claim-type and depth controls are gone; the stored
  // config keeps its defaults (auto-detect, moderate).
  const [config] = useSessionState(configKey, {
    claimType: null as string | null,
    aggressiveness: 'moderate',
  });
  const { claimType, aggressiveness } = config;

  // Reason: dismissedIds is a Set, which doesn't survive JSON. Persist as
  // array, hydrate back to Set via getter/setter shim.
  const [dismissedArray, setDismissedArray] = useSessionState<string[]>(dismissedKey, []);
  const dismissedIds = React.useMemo(() => new Set(dismissedArray), [dismissedArray]);
  const setDismissedIds = useCallback(
    (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setDismissedArray((prevArr) => {
        const prevSet = new Set(prevArr);
        const next = typeof updater === 'function' ? updater(prevSet) : updater;
        return Array.from(next);
      });
    },
    [setDismissedArray],
  );

  // --- Transient state (NOT persisted — re-derived or short-lived) ---
  // selectedFile: File objects are not JSON-serializable; user re-selects on reload.
  // isLoading/error/copied: transient UI feedback, no value in persisting.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [esxBusy, setEsxBusy] = useState(false);
  const [esxError, setEsxError] = useState<string | null>(null);

  // --- Post-parse project create/match dialog ---
  // Header metadata extracted from the PDF and the matcher result are
  // transient: we want a fresh match every parse, and once the user has
  // dismissed the dialog they shouldn't see it again until the next parse.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogHeader, setDialogHeader] = useState<EstimateHeaderMetadata | null>(null);
  const [dialogMatch, setDialogMatch] = useState<ProjectMatchResponse | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedProject,
    setSelectedProject,
    getProjectById,
    projects,
    fetchProjects,
  } = useProjectStore();

  // Prune stale per-project sessionStorage on mount: keys whose UUID
  // doesn't correspond to a current project (project was deleted) are
  // dead weight that would re-surface if the user re-navigated to the
  // old project URL. Also drops the legacy `__no_project__` bucket
  // from the pre-PRP-supp shape.
  useEffect(() => {
    const run = async () => {
      let current = projects;
      if (current.length === 0) {
        try {
          await fetchProjects();
          current = useProjectStore.getState().projects;
        } catch {
          return; // skip cleanup rather than risk dropping live buckets
        }
      }
      const live = new Set(current.map((p) => p.id));
      const toDrop: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (!k) continue;
        if (k.startsWith('completeness:')) {
          const parts = k.split(':');
          const tail = parts[parts.length - 1];
          if (tail === '__no_project__' || (tail && !live.has(tail))) {
            toDrop.push(k);
          }
        }
      }
      for (const k of toDrop) window.sessionStorage.removeItem(k);
    };
    run();
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reason: load the current project so the dialog can render its name
  // in the "selected + no match" / "selected + conflict" states.
  useEffect(() => {
    if (!projectId) return;
    if (selectedProject?.id === projectId) return;
    getProjectById(projectId);
  }, [projectId, selectedProject, getProjectById]);

  /**
   * Centralized post-parse hook: extract header_metadata, run /projects/match,
   * decide whether to surface the dialog. Errors are swallowed so the page
   * falls back to plain parse-output rendering.
   */
  const runProjectMatch = useCallback(
    async (header?: EstimateHeaderMetadata | null) => {
      if (!header) return;
      // Bail out if there's no useful signal to match on.
      const hasSignal =
        header.claim_number ||
        header.owner_name ||
        header.address?.street;
      if (!hasSignal) return;

      let matchResult: ProjectMatchResponse | null = null;
      try {
        matchResult = await BackendAPI.matchProject(header);
      } catch (err) {
        // Reason: match endpoint is best-effort; on failure we still show
        // the dialog without suggestions rather than blocking.
        console.warn('[completeness] project match failed:', err);
        matchResult = { exact_match: null, fuzzy_matches: [] };
      }

      // Obvious-answer shortcut: selected project IS the exact match.
      // Skip the dialog entirely — there's nothing to confirm.
      if (
        selectedProject &&
        matchResult.exact_match &&
        matchResult.exact_match.id === selectedProject.id
      ) {
        return;
      }

      setDialogHeader(header);
      setDialogMatch(matchResult);
      setDialogOpen(true);
    },
    [selectedProject],
  );

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  // Scratchpad → pinned transition: when the user matches/creates a
  // project from the dialog, the URL flips from no-project to
  // ?project=<id>, which re-keys useSessionState. Without seeding the
  // new bucket first, the just-rendered report/parse output would
  // vanish on re-render. Write the current in-memory state into the
  // new project's slots before navigating.
  const adoptScratchpadIntoProject = useCallback(
    (projectIdNew: string) => {
      try {
        if (report !== null) {
          window.sessionStorage.setItem(
            `completeness:report:${projectIdNew}`,
            JSON.stringify(report),
          );
        }
        if (parsed !== null) {
          window.sessionStorage.setItem(
            `completeness:parsed:${projectIdNew}`,
            JSON.stringify(parsed),
          );
        }
        window.sessionStorage.setItem(
          `completeness:config:${projectIdNew}`,
          JSON.stringify(config),
        );
        if (dismissedArray.length > 0) {
          window.sessionStorage.setItem(
            `completeness:dismissed:${projectIdNew}`,
            JSON.stringify(dismissedArray),
          );
        }
      } catch {
        // Quota / serialization failure: not fatal. The state lives in
        // memory and the user can re-analyze.
      }
    },
    [report, parsed, config, dismissedArray],
  );

  const handleDialogUseProject = useCallback(
    (project: Project) => {
      setDialogOpen(false);
      setSelectedProject(project);
      adoptScratchpadIntoProject(project.id);
      const params = new URLSearchParams(location.search);
      params.set('project', project.id);
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [navigate, location, setSelectedProject, adoptScratchpadIntoProject],
  );

  const handleDialogProjectCreated = useCallback(
    (project: Project) => {
      setDialogOpen(false);
      setSelectedProject(project);
      adoptScratchpadIntoProject(project.id);
      const params = new URLSearchParams(location.search);
      params.set('project', project.id);
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [navigate, location, setSelectedProject, adoptScratchpadIntoProject],
  );

  // --- Handlers ---

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setReport(null);
    setParsed(null);
    setError(null);
    setDismissedIds(new Set());
  }, []);

  // Stage 1 — faithful import. Every upload is parsed faithfully (no
  // enhancement) and shown in the verification view. Suggestions are an
  // explicit, opt-in second step (handleConfirmParse → "Suggest
  // enhancements"). B-035: faithful reproduction first, enhancement on demand.
  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError(null);
    setReport(null);
    setDismissedIds(new Set());

    try {
      const p = await BackendAPI.parseEstimate(selectedFile);
      setParsed(p);
      // Don't block parse render on project-match: fire and forget.
      runProjectMatch(p.header_metadata ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, runProjectMatch]);

  const handleConfirmParse = useCallback(async () => {
    if (!parsed) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await BackendAPI.analyzeCachedEstimate(
        parsed.parse_hash,
        claimType ?? undefined,
        aggressiveness,
      );
      setReport(result);
      setParsed(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analysis failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [parsed, claimType, aggressiveness]);

  const handleReupload = useCallback(() => {
    setParsed(null);
    setSelectedFile(null);
    setReport(null);
    setError(null);
  }, []);

  const handleDismiss = useCallback((id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
  }, []);

  const handleRecover = useCallback((id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleCopyToClipboard = useCallback(() => {
    if (!report) return;
    const lines: string[] = [];
    for (const room of report.rooms) {
      const activeSuggestions = room.suggestions.filter(
        (s) => !dismissedIds.has(s.suggestion_id)
      );
      if (activeSuggestions.length === 0) continue;
      lines.push(`=== ${room.room_name} ===`);
      for (const s of activeSuggestions) {
        lines.push(`${s.xactimate_code}\t${s.description}`);
      }
      lines.push('');
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [report, dismissedIds]);

  // --- Computed ---

  const orderedRooms = report ? orderRoomsByClaimType(report.rooms, report.claim_type) : [];

  const totalSuggestions = report
    ? report.rooms.reduce((sum, r) => sum + r.suggestions.length, 0)
    : 0;

  const suggestionsByTier = report
    ? report.rooms.reduce(
        (acc, r) => {
          for (const s of r.suggestions) {
            if (!dismissedIds.has(s.suggestion_id)) {
              acc[s.tier] = (acc[s.tier] || 0) + 1;
            }
          }
          return acc;
        },
        {} as Record<string, number>
      )
    : {};

  const totalExisting = report
    ? report.rooms.reduce((sum, r) => sum + r.existing_items.length, 0)
    : 0;

  // --- Render ---

  // Download the matched scope from the uploaded estimate as an importable
  // .ESX. Reuses the proven PDF->ESX path: the backend parses the file,
  // matches catalog codes, and returns the SCOPE as ESX (not the raw PDF).
  const handleDownloadEsx = useCallback(async () => {
    if (!selectedFile) return;
    setEsxBusy(true);
    setEsxError(null);
    try {
      const res = await BackendAPI.generateEsxFromPdf(selectedFile);
      triggerEsxDownload(res.filename, res.esx_b64);
    } catch (err) {
      setEsxError(err instanceof Error ? err.message : String(err));
    } finally {
      setEsxBusy(false);
    }
  }, [selectedFile]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">Supplement</span>
      </nav>

      {/* Page header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-primary" />
          Supplement
        </h2>
        <p className="text-muted-foreground">
          Upload an estimate for a faithful import — exactly what's in the PDF. Download it as an ESX, or
          run an optional enhancement pass to review suggested additions.
        </p>
      </div>

      {/* Upload & controls card */}
      <div className="rounded-lg border bg-white p-6 shadow-sm space-y-5">
        <UploadArea
          onFileSelect={handleFileSelect}
          isLoading={isLoading}
          selectedFile={selectedFile}
          onAnalyze={handleAnalyze}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Stage 1 — faithful import view. Download the faithful ESX here, or
          opt in to the enhancement pass. Suggestions never appear until asked. */}
      {parsed && !report && (
        <ParseVerificationView
          parsed={parsed}
          onConfirm={handleConfirmParse}
          onReupload={handleReupload}
          isAnalyzing={isLoading}
          onParsedUpdate={setParsed}
          onDownloadEsx={selectedFile ? handleDownloadEsx : undefined}
          esxBusy={esxBusy}
          esxError={esxError}
        />
      )}

      {/* Results */}
      {report && (
        <div className="space-y-6">
          {/* Parser diagnostics — color-coded banner with expandable details */}
          {(report.parser_diagnostics || report.parse_summary) && (
            <ParserDiagnosticsBanner
              diagnostics={(report.parser_diagnostics || report.parse_summary)!}
            />
          )}
          {/* Parse warnings */}
          {(report.parse_warnings.length > 0 || report.match_stats.unmatched > 0) && (
            <ParseWarningBanner
              warnings={report.parse_warnings}
              matchStats={report.match_stats}
            />
          )}

          {/* Summary stats bar */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
            <StatCard label="Rooms" value={report.rooms.length} />
            <StatCard label="Existing Items" value={totalExisting} />
            <StatCard label="Total Suggestions" value={totalSuggestions} />
            <StatCard label="Required" value={suggestionsByTier.required || 0} color="text-green-700" />
            <StatCard label="Recommended" value={suggestionsByTier.recommended || 0} color="text-yellow-700" />
            <StatCard label="Consider" value={suggestionsByTier.consider || 0} color="text-blue-700" />
          </div>

          {/* Actions — shown only after Analyze, once the scope is on screen.
              "Faithful" exports the parsed estimate as-is to ESX. (The AI-
              enhanced export — supplements added, then ESX — is the deferred
              enhancement flow and is not wired here yet.) */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {selectedFile && (
              <button
                type="button"
                onClick={handleDownloadEsx}
                disabled={esxBusy}
                className="flex items-center gap-2 rounded-md border border-primary bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                title="Export the parsed estimate as an importable Xactimate .ESX (faithful — no AI enhancement)"
              >
                {esxBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download Faithful Scope as ESX
              </button>
            )}
            {esxError && (
              <p className="text-sm text-red-700">ESX export failed: {esxError}</p>
            )}
            <button
              onClick={handleCopyToClipboard}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-50"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Suggestions
                </>
              )}
            </button>
          </div>

          {/* Room sections */}
          <div className="space-y-4">
            {orderedRooms.map((room, index) => (
              <RoomSection
                key={index}
                room={room}
                onDismiss={handleDismiss}
                onRecover={handleRecover}
                dismissedIds={dismissedIds}
              />
            ))}
          </div>

          {/* Out of coverage */}
          {report.out_of_coverage.length > 0 && (
            <OutOfCoverageSection
              items={report.out_of_coverage}
              claimType={report.claim_type}
            />
          )}
        </div>
      )}

      {/* Post-parse project create/match dialog — rendered on top of parse
          output, never blocks it. */}
      {dialogOpen && dialogHeader && (
        <PostParseProjectDialog
          header={dialogHeader}
          match={dialogMatch}
          currentProject={selectedProject ?? null}
          onUseProject={handleDialogUseProject}
          onProjectCreated={handleDialogProjectCreated}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
}

// ================================================================= //
//          Helper: Stat Card                                          //
// ================================================================= //

function StatCard({
  label,
  value,
  color = 'text-foreground',
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div className="rounded-lg border bg-white p-3 text-center shadow-sm">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// ================================================================= //
//          Helper: Room Ordering by Claim Type                        //
// ================================================================= //

/**
 * Orders rooms based on claim type convention.
 * Flood claims: reverse order (ground-up).
 * All other claims: keep original order (top-down).
 *
 * Note: Per locked decision, this primarily affects ITEM ordering within
 * rooms. For room-level ordering, flood claims reverse the default order
 * (assuming rooms come top-down from the estimate).
 */
function orderRoomsByClaimType(
  rooms: CompletenessReport['rooms'],
  claimType: string,
): CompletenessReport['rooms'] {
  const isFlood = claimType.toLowerCase() === 'flood';
  if (isFlood) {
    // Reason: Flood claims follow NFIP ground-up convention
    return [...rooms].reverse();
  }
  return rooms;
}
